import { c as _c } from "react/compiler-runtime";
import { MutationConfig, QueryConfig, QueryConfigPickRes, queryClient } from 'api/reactQuery';
import { MINUTE_MS } from 'constants/global';
import { DetailedPlanQueryParams, fetchEquipmentAvailability, fetchOnePlan, fetchPlanCheckout, fetchPlanIndividualBookingItemSelection, fetchPlanInventory, postPlanGroups } from './api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type EquipmentAvailabilityResponse, type IndividualBookingItem } from './types';
import { type postBooking } from '../bookings/api';
import { usePostBooking } from '../bookings/hooks';
import { invalidateBookingLimits } from '../accounts/bookingLimitsCache';
export const makePlanQueryKeys = {
  fetchMany: 'plans',
  fetchOne: (planId: string | undefined, queryParams: DetailedPlanQueryParams = {}) => [makePlanQueryKeys.fetchMany, planId, queryParams],
  fetchPlanCheckout: (planId: string | undefined, queryParams: Parameters<typeof fetchPlanCheckout>[1]) => [makePlanQueryKeys.fetchMany, planId, 'checkout', queryParams],
  fetchPlanIndividualBookingItemSelection: (planId: string | undefined, dateOptions: {
    startDate?: string;
    endDate?: string;
  } = {}) => [makePlanQueryKeys.fetchMany, planId, 'individualBookingItemSelection', dateOptions],
  availability: (planId: string | undefined, accountId: string | undefined, inventoryDetailId?: string) => [makePlanQueryKeys.fetchMany, planId, 'availability', accountId,
  // Omitted rather than passed as undefined so the unfiltered key stays a prefix
  // of the filtered one, letting a plan-level invalidation reach both.
  ...(inventoryDetailId ? [inventoryDetailId] : [])] as const
};
export const useDetailedPlan = (planId, queryParams, options) => {
  const $ = _c(10);
  let t0;
  if ($[0] !== planId || $[1] !== queryParams) {
    t0 = makePlanQueryKeys.fetchOne(planId, queryParams);
    $[0] = planId;
    $[1] = queryParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== planId || $[4] !== queryParams) {
    t1 = async () => fetchOnePlan(planId, {
      ...queryParams,
      relations: ["planFaqs", "service", "service.provider", "regions", "planBookingFields", "merchant"],
      fields: ["merchant.id", "merchant.legalName", "merchant.termsOfService", "merchant.termsOfServiceLink", "merchant.currency"]
    });
    $[3] = planId;
    $[4] = queryParams;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== options || $[7] !== t0 || $[8] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      ...options
    };
    $[6] = options;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useQuery(t2);
};
export const useFetchCheckout = (planId, queryParams, options) => {
  const $ = _c(10);
  let t0;
  if ($[0] !== planId || $[1] !== queryParams) {
    t0 = makePlanQueryKeys.fetchPlanCheckout(planId, queryParams);
    $[0] = planId;
    $[1] = queryParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== planId || $[4] !== queryParams) {
    t1 = async () => {
      const {
        records
      } = await fetchPlanCheckout(planId, queryParams);
      return records;
    };
    $[3] = planId;
    $[4] = queryParams;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== options || $[7] !== t0 || $[8] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      ...options
    };
    $[6] = options;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useQuery(t2);
};
export const useCreatePlanGroups = (planId, options) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== planId) {
    t0 = body => postPlanGroups(planId, body);
    $[0] = planId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export const usePlanIndividualBookingItemSelection = (planId, dateOptions, options) => {
  const $ = _c(11);
  let t0;
  if ($[0] !== dateOptions || $[1] !== planId) {
    t0 = makePlanQueryKeys.fetchPlanIndividualBookingItemSelection(planId, dateOptions);
    $[0] = dateOptions;
    $[1] = planId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== dateOptions || $[4] !== planId) {
    t1 = async () => {
      if (!planId) {
        throw Error("Missing plan id");
      }
      return fetchPlanIndividualBookingItemSelection(planId, dateOptions);
    };
    $[3] = dateOptions;
    $[4] = planId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = !!planId;
  let t3;
  if ($[6] !== options || $[7] !== t0 || $[8] !== t1 || $[9] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      ...options
    };
    $[6] = options;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return useQuery(t3);
};
export const usePlanInventory = (planId, params, options) => {
  const $ = _c(9);
  let t0;
  let t1;
  if ($[0] !== params || $[1] !== planId) {
    t0 = [makePlanQueryKeys.fetchMany, planId, "inventory", params];
    t1 = async () => {
      if (!planId) {
        throw Error("Missing plan id");
      }
      return fetchPlanInventory(planId, params);
    };
    $[0] = params;
    $[1] = planId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  const t2 = Boolean(planId) && Boolean(params.accountId);
  let t3;
  if ($[4] !== options || $[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      ...options
    };
    $[4] = options;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
};
export const useBookPlan = options => {
  const $ = _c(5);
  let t0;
  if ($[0] !== options) {
    t0 = async (...t1) => {
      const args = t1;
      options?.onSettled?.(...args);
      const [,, variables] = args;
      if (variables) {
        await Promise.all([queryClient.invalidateQueries({
          queryKey: makePlanQueryKeys.fetchOne(variables.planId)
        }), queryClient.invalidateQueries({
          queryKey: makePlanQueryKeys.availability(variables.planId, variables.accountId)
        }), invalidateBookingLimits(variables.accountId)]);
      }
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      ...options,
      onSettled: t0
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return usePostBooking(t1);
};
export const useEquipmentAvailability = (planId, accountId, inventoryDetailId, options) => {
  const $ = _c(13);
  let t0;
  if ($[0] !== accountId || $[1] !== inventoryDetailId || $[2] !== planId) {
    t0 = makePlanQueryKeys.availability(planId, accountId, inventoryDetailId);
    $[0] = accountId;
    $[1] = inventoryDetailId;
    $[2] = planId;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== accountId || $[5] !== inventoryDetailId || $[6] !== planId) {
    t1 = async () => {
      if (!planId || !accountId) {
        throw Error("Missing plan or account id");
      }
      return fetchEquipmentAvailability(planId, accountId, inventoryDetailId);
    };
    $[4] = accountId;
    $[5] = inventoryDetailId;
    $[6] = planId;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const t2 = Boolean(planId) && Boolean(accountId);
  let t3;
  if ($[8] !== options || $[9] !== t0 || $[10] !== t1 || $[11] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 5 * MINUTE_MS,
      ...options
    };
    $[8] = options;
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return useQuery(t3);
};